import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { switchOptions as reportSwitchOptions, type DashboardConfig } from '@/features/dashboard/config';
import { useMinionsReport } from '@/features/minions/useMinionsReport';
import { useMinionsI18n } from '@/features/minions/i18n';
import { fmtClock } from '@/features/minions/format';
import { downloadCsv, weeklySummaryCsv } from '@/features/minions/csv';
import { MinionsI18nProvider } from '@/features/minions/components/MinionsI18nProvider';
import { Header } from '@/features/minions/components/Header';
import { DailyCard, HeadlineTiles, ReachCard, TakeAway } from '@/features/minions/components/Overview';
import {
  ConsentsCard,
  DauWauCard,
  DistributionCard,
  LoginMissionCard,
  ReferralCard,
  RetentionCard,
  TimeCard,
  WeeklyPlayersCard,
  WeeklySummary,
} from '@/features/minions/components/Engagement';
import '@/features/minions/minions.css';

/*
 * Système U — Minions — Pinball (program su04, "Mission Incognito"): thin
 * orchestrator. Fetch (the sample figures in dev until the report answers),
 * then the sections in the design's order: take-away, reach and funnel,
 * headline tiles, daily, engagement and retention, missions, consents and
 * time, weekly summary. Whole campaign throughout — one period, 3 Nov → 6 Dec.
 *
 * French or English (header toggle); the provider remembers the choice.
 */
export function MinionsDashboardPage({ config }: { config: DashboardConfig }) {
  return (
    <MinionsI18nProvider>
      <MinionsDashboard config={config} />
    </MinionsI18nProvider>
  );
}

function MinionsDashboard({ config }: { config: DashboardConfig }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lang, t, f } = useMinionsI18n();
  const { data, isPreview, lastRefreshedAt } = useMinionsReport(config.reportId, config.apiBaseUrl);

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="mn" lang={lang}>
      <Header
        campaign={data.campaign}
        isPreview={isPreview}
        updatedAt={fmtClock(lastRefreshedAt, data.campaign.timezone)}
        onExport={() => downloadCsv(`systeme-u-minions-2026-weekly-${lang}.csv`, weeklySummaryCsv(data, t))}
        switchOptions={reportSwitchOptions(auth?.allowedReports ?? [], auth?.role === 'admin', config.reportId)}
        onSwitch={(p) => navigate(p)}
        isAdmin={auth?.role === 'admin'}
        onManageUsers={() => navigate('/admin/users')}
        onSignOut={onSignOut}
      />
      <main className="mn-wrap mn-main">
        <TakeAway data={data} />
        <ReachCard data={data} />
        <HeadlineTiles data={data} />
        <DailyCard data={data} />

        <h2 className="mn-section">{t.sEngagement}</h2>
        <div className="mn-grid">
          <DauWauCard data={data} />
          <RetentionCard data={data} />
        </div>
        <div className="mn-grid">
          <WeeklyPlayersCard data={data} />
          <DistributionCard data={data} />
        </div>

        <h2 className="mn-section">{t.sMissions}</h2>
        <div className="mn-grid">
          <LoginMissionCard data={data} />
          <ReferralCard data={data} />
        </div>

        <div className="mn-grid">
          <ConsentsCard data={data} />
          <TimeCard data={data} />
        </div>

        <WeeklySummary data={data} />

        <p className="mn-foot">{t.foot(f.period(data.campaign.start, data.campaign.end))}</p>
      </main>
    </div>
  );
}
